import { FServiceAPI } from '@freelog/tools-lib';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import fMessage from '@/components/fMessage';

// ─── Types ───────────────────────────────────────────────────────────

export type HandleRelationParams = {
  contracts: {
    contractId: string;
    policyId: string;
  }[];
  resourceId: string;
  resourceName: string;
}[];

export type HandleRelationResult = {
  resourceId: string;
  resourceName: string;
  resourceType: string[];
  status: 0 | 1 | 2 | 4;
  contracts: {
    contractId: string;
    contractName: string;
    createDate: string;
    policyText: string;
    status: 'active' | 'testActive' | 'inactive' | 'terminal';
    policyId: string;
  }[];
  terminatedContractIDs: string[];
  policies: PolicyFullInfo_Type[];
}[];

interface GetAllContractsParams {
  nodeID: number;
  resourceIDs: string[];
}

type GetAllContractsReturn = {
  contractId: string;
  contractName: string;
  createDate: string;
  updateDate: string;
  policyId: string;
  policyInfo: {
    policyId: string;
    policyText: string;
  };
}[];

export interface UpdateRewriteParams {
  exhibit_ID: string;
  inheritOptions: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  customOptions: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
}

// ─── Pure helpers ────────────────────────────────────────────────────

export async function getAllContracts({
  nodeID,
  resourceIDs,
}: GetAllContractsParams): Promise<GetAllContractsReturn> {
  const allPromises = resourceIDs.map(async (id) => {
    const params: Parameters<typeof FServiceAPI.Contract.batchContracts>[0] = {
      licensorId: id,
      licenseeId: nodeID,
      licenseeIdentityType: 2,
      isLoadPolicyInfo: 1,
      subjectIds: id,
      subjectType: 1,
    };
    const { data } = await FServiceAPI.Contract.batchContracts(params);
    return data;
  });
  return (await Promise.all(allPromises)).flat();
}

export async function handleRelation(
  params: HandleRelationParams,
  nodeID: number,
): Promise<HandleRelationResult> {
  const resourceIds: string[] = params.map((r) => r.resourceId);

  const allContracts = await getAllContracts({ nodeID, resourceIDs: resourceIds });

  const params0: Parameters<typeof FServiceAPI.Resource.batchInfo>[0] = {
    resourceIds: resourceIds.join(','),
    isLoadPolicyInfo: 1,
    isTranslate: 1,
  };
  const { data: data0 }: any = await FServiceAPI.Resource.batchInfo(params0);

  return params.map((r) => {
    const resource = data0.find((dr: any) => dr.resourceId === r.resourceId);
    const contracts: HandleRelationResult[number]['contracts'] = allContracts
      .filter((acts: any) => acts.licensorId === resource.resourceId && acts.status === 0)
      .map((contract: any) => ({
        contractId: contract.contractId,
        contractName: contract.contractName,
        createDate: contract.createDate,
        policyText: contract.policyInfo.policyText,
        status:
          contract.status === 1
            ? 'terminal'
            : contract.authStatus === 1 || contract.authStatus === 3
            ? 'active'
            : contract.authStatus === 2
            ? 'testActive'
            : 'inactive',
        policyId: contract.policyId,
      }));
    const allContractsUsedPolicyIDs: string[] = contracts.map<string>((c) => c.policyId);
    return {
      resourceId: resource.resourceId,
      resourceName: resource.resourceName,
      resourceType: resource.resourceType,
      status: resource.status,
      contracts,
      terminatedContractIDs: allContracts
        .filter((acts: any) => acts.licensorId === resource.resourceId && acts.status === 1)
        .map((acts: any) => acts.contractId),
      policies: resource.policies.filter((p: any) => {
        return p.status === 1 && !allContractsUsedPolicyIDs.includes(p.policyId);
      }),
    };
  });
}

/**
 * Fetch exhibit details and return policies sorted by status (active first).
 */
export async function fetchAndSortPolicies(
  exhibitId: string,
): Promise<{ policies: PolicyFullInfo_Type[]; onlineStatus: number }> {
  const params: Parameters<typeof FServiceAPI.Exhibit.presentableDetails>[0] = {
    presentableId: exhibitId,
    isLoadCustomPropertyDescriptors: 1,
    isLoadPolicyInfo: 1,
    isTranslate: 1,
  };
  const { data } = await FServiceAPI.Exhibit.presentableDetails(params);
  const policies: PolicyFullInfo_Type[] = [...data.policies];
  policies.reverse();
  policies.sort((a, b) => (a.status === 1 && b.status === 0 ? -1 : 0));
  return { policies, onlineStatus: data.onlineStatus };
}

/**
 * Add a policy to an exhibit. Returns false if API call failed.
 */
export async function addExhibitPolicy(
  exhibitId: string,
  title: string,
  text: string,
): Promise<boolean> {
  const params: Parameters<typeof FServiceAPI.Exhibit.updatePresentable>[0] = {
    presentableId: exhibitId,
    addPolicies: [{ policyName: title, policyText: text, status: 1 }],
  };
  const res: { ret: number; errCode: number; msg: string } =
    await FServiceAPI.Exhibit.updatePresentable(params);
  if (res.ret !== 0 || res.errCode !== 0) {
    fMessage(res.msg, 'error');
    return false;
  }
  return true;
}

/**
 * Update a policy's status on an exhibit.
 */
export async function updateExhibitPolicyStatus(
  exhibitId: string,
  policyId: string,
  status: 0 | 1,
): Promise<void> {
  const params: Parameters<typeof FServiceAPI.Exhibit.updatePresentable>[0] = {
    presentableId: exhibitId,
    updatePolicies: [{ policyId, status }],
  };
  await FServiceAPI.Exhibit.updatePresentable(params);
}

/**
 * Update exhibit online/offline status. Returns false on failure.
 */
export async function updateExhibitOnlineStatus(
  exhibitId: string,
  onlineStatus: 0 | 1,
): Promise<boolean> {
  const params: Parameters<typeof FServiceAPI.Exhibit.presentablesOnlineStatus>[0] = {
    presentableId: exhibitId,
    onlineStatus,
  };
  const { data } = await FServiceAPI.Exhibit.presentablesOnlineStatus(params);
  return !!data;
}

/**
 * Update exhibit base info (title, tags, cover, intro).
 */
export async function updateExhibitBaseInfo(
  exhibitId: string,
  info: {
    presentableTitle?: string;
    tags?: string[];
    presentableIntro?: string;
    coverImages?: string[];
  },
): Promise<{ success: boolean; msg?: string }> {
  const params: Parameters<typeof FServiceAPI.Exhibit.updatePresentable>[0] = {
    presentableId: exhibitId,
    ...info,
  };
  const { ret, errCode, msg } = await FServiceAPI.Exhibit.updatePresentable(params);
  if (ret !== 0 || errCode !== 0) {
    return { success: false, msg };
  }
  return { success: true };
}

/**
 * Save exhibit introduction with optimistic update support.
 */
export async function saveExhibitIntroduction(
  exhibitId: string,
  value: string,
): Promise<{ success: boolean; msg?: string }> {
  const params: Parameters<typeof FServiceAPI.Exhibit.updatePresentable>[0] = {
    presentableId: exhibitId,
    presentableIntro: value,
  };
  const { ret, errCode, msg } = await FServiceAPI.Exhibit.updatePresentable(params);
  if (ret !== 0 || errCode !== 0) {
    return { success: false, msg };
  }
  return { success: true };
}

/**
 * Update exhibit rewrite properties (inherit + custom options).
 */
export async function updateRewrite({
  exhibit_ID,
  inheritOptions,
  customOptions,
}: UpdateRewriteParams): Promise<any> {
  const params: Parameters<typeof FServiceAPI.Exhibit.updateRewriteProperty>[0] = {
    presentableId: exhibit_ID,
    rewriteProperty: [
      ...inheritOptions.map((io) => ({
        key: io.key,
        name: io.name,
        value: io.value,
        remark: io.description,
      })),
      ...customOptions.map((io) => ({
        key: io.key,
        name: io.name,
        value: io.value,
        remark: io.description,
      })),
    ],
  };
  return FServiceAPI.Exhibit.updateRewriteProperty(params);
}

/**
 * Change exhibit version.
 */
export async function changeExhibitVersion(
  exhibitId: string,
  version: string,
): Promise<{ success: boolean; msg?: string }> {
  const params: Parameters<typeof FServiceAPI.Exhibit.presentablesVersion>[0] = {
    presentableId: exhibitId,
    version,
  };
  const { ret, errCode, msg }: { ret: number; errCode: number; msg: string } =
    await FServiceAPI.Exhibit.presentablesVersion(params);
  if (ret !== 0 || errCode !== 0) {
    return { success: false, msg };
  }
  return { success: true };
}
